import { useCallback } from 'react';
import type { MutationOptions, UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import type { ApiSchemaUnshortlistEnquiries } from 'Services/api/schema.types';
import { useShouldRefreshVehicleList } from 'Stores/ListStore/ListStore';
import {
	useClearUnshortlistCache,
	useRemoveEnquiriesFromShortlist,
	useSetIsWaitOnUnshortlistResponse,
	useUnshortlistCache,
	useUnshortlistFromCache,
} from 'Stores/ShortlistStore/ShortlistStore';
import { handleAxiosError } from 'Utilities/error/handleAxiosError';

type MutationResponse = ApiSchemaUnshortlistEnquiries;
type MutationError = {
	config?: {
		data?: string;
	};
};

const timesToRetry = 3;

export const useUnshortlistVehicleMutation = (
	refreshVehicleList: () => void,
): UseMutationResult<MutationResponse, MutationError, unknown> => {
	const unshortlistFromCache = useUnshortlistFromCache();
	const unshortlistCache = useUnshortlistCache();
	const setIsWaitingOnUnshortlistResponse = useSetIsWaitOnUnshortlistResponse();
	const removeEnquiriesFromShortlist = useRemoveEnquiriesFromShortlist();
	const clearUnshortlistCache = useClearUnshortlistCache();
	const shouldRefreshVehicleList = useShouldRefreshVehicleList();

	const synchroniseShortlistStates = useCallback(
		(unshortlistedResponseData: ApiSchemaUnshortlistEnquiries['data']) => {
			if (shouldRefreshVehicleList) {
				refreshVehicleList();
			}
			removeEnquiriesFromShortlist(unshortlistedResponseData); // synchronises shortlist tab with other tabs
			clearUnshortlistCache();
		},
		[clearUnshortlistCache, refreshVehicleList, removeEnquiriesFromShortlist, shouldRefreshVehicleList],
	);

	const onSettled = () => {
		setIsWaitingOnUnshortlistResponse(false);
	};

	const onSuccess = (data: MutationResponse): void => {
		if (data?.data) {
			synchroniseShortlistStates(data.data);
		}
	};

	const onError = (error: MutationError): void => {
		clearUnshortlistCache();
		handleAxiosError({
			axiosError: error as AxiosError,
			sourceError: {
				message: `There was an issue with unshortlisting vehicles ${unshortlistCache}`,
				name: 'useUnshortlistVehicleMutation',
			},
		});
	};

	return useMutation({
		mutationFn: unshortlistFromCache,
		onError,
		onSettled,
		onSuccess,
		retry: timesToRetry,
	} as MutationOptions<MutationResponse, MutationError, unknown>);
};
